import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const formatTime = (date, manual = false) => {
  if (!(date instanceof Date)) {
    return '';
  }
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 767;
  try {
    if (isMobile) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}, ${hours}:${minutes}${manual ? ' (Manual)' : ''}`;
    }
    const options = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    const value = date.toLocaleDateString('es-ES', options);
    return manual ? `${value} (Manual)` : value;
  } catch (error) {
    console.warn('Locale fallback for clock', error);
    const fallback = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    return manual ? `${fallback} (Manual)` : fallback;
  }
};

export const useClock = () => {
  const [time, setTime] = useState(() => new Date());
  const [isManual, setIsManual] = useState(false);
  const [isFrozen, setIsFrozen] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    if (!isManual && !isFrozen) {
      setTime(new Date());
    }
  }, [isManual, isFrozen]);

  useEffect(() => {
    if (isFrozen) {
      clearTimer();
      return undefined;
    }
    timerRef.current = setInterval(tick, 30000);
    return () => clearTimer();
  }, [tick, isFrozen, clearTimer]);

  const setManualTime = useCallback((date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return;
    }
    setTime(date);
    setIsManual(true);
    clearTimer();
  }, [clearTimer]);

  const resetToAuto = useCallback(() => {
    const now = new Date();
    setIsManual(false);
    setTime(now);
    setIsFrozen(false);
  }, []);

  const freezeClock = useCallback((date) => {
    setIsFrozen(true);
    clearTimer();
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      setTime(date);
    }
  }, [clearTimer]);

  const resumeClock = useCallback(() => {
    setIsFrozen(false);
    if (!isManual) {
      setTime(new Date());
    }
  }, [isManual]);

  const formatLabel = useCallback((manualFlag = isManual) => formatTime(time, manualFlag), [time, isManual]);

  const value = useMemo(() => ({
    time,
    isManual,
    isFrozen,
    label: formatLabel(),
    formatLabel,
    setManualTime,
    resetToAuto,
    freezeClock,
    resumeClock
  }), [time, isManual, isFrozen, formatLabel, setManualTime, resetToAuto, freezeClock, resumeClock]);

  return value;
};
